import { useEffect, useMemo, useState } from "react";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { observer } from "mobx-react-lite";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import { FormField } from "@/components/common";
import { Button } from "@/components/ui/button";
import { useAuthStore } from "@/features/auth";
import { useTran } from "@/hooks";
import { PATHS } from "@/lib/router";
import { esgService } from "../services";

const REQUIRES_PROFILE_ERROR = "name, username and password are required";
const USERNAME_ALLOWED_REGEX = /^[a-z0-9]+$/;

const buildProfileSchema = (t) =>
  Yup.object({
    name: Yup.string().trim().min(2).max(120).required(t("Name is required")),
    username: Yup.string()
      .trim()
      .min(3)
      .max(100)
      .matches(
        USERNAME_ALLOWED_REGEX,
        t(
          "Username can only contain lowercase letters and numbers without spaces",
        ),
      )
      .required(t("Username is required")),
    password: Yup.string().min(8).required(t("Password is required")),
  });

const isProfileRequiredError = (message) =>
  String(message || "")
    .toLowerCase()
    .includes(REQUIRES_PROFILE_ERROR);

const ESGInviteAcceptPage = observer(() => {
  const { t } = useTran();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuthStore();

  const [mode, setMode] = useState("resolving");
  const [errorMessage, setErrorMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const token = (searchParams.get("token") || "").trim();
  const profileSchema = useMemo(() => buildProfileSchema(t), [t]);

  const submitInviteAcceptance = async (payload = {}) => {
    if (!token) {
      setMode("error");
      setErrorMessage(t("Invite token is missing."));
      return;
    }

    setSubmitting(true);
    try {
      await esgService.acceptEsgInvite({
        token,
        ...payload,
      });
      setMode("success");
      setErrorMessage("");
    } catch (error) {
      const message = error?.message || t("Unable to accept invite.");
      if (isProfileRequiredError(message)) {
        setMode("create_account");
        setErrorMessage("");
      } else if (mode === "create_account") {
        // Stay on the form so the user can fix validation errors (e.g. duplicate username)
        setErrorMessage(message);
      } else {
        setMode("error");
        setErrorMessage(message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    setMode("resolving");
    setErrorMessage("");
    submitInviteAcceptance();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const renderBody = () => {
    if (mode === "resolving") {
      return (
        <p className="text-sm text-muted-foreground">
          {t("Validating invitation link...")}
        </p>
      );
    }

    if (mode === "success") {
      return (
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            {t("Invitation accepted successfully.")}
          </p>
          <div className="flex gap-2">
            <Button
              className="flex-1"
              onClick={() =>
                navigate(user ? PATHS.ESG_REPORT_PREVIEW_LIST : PATHS.LOGIN, {
                  replace: true,
                })
              }
            >
              {t(user ? "Go to Reports" : "Go to Login")}
            </Button>
          </div>
        </div>
      );
    }

    if (mode === "create_account") {
      return (
        <Formik
          initialValues={{ name: "", username: "", password: "" }}
          validationSchema={profileSchema}
          onSubmit={async (values) => {
            await submitInviteAcceptance(values);
          }}
        >
          {({ errors, touched, values, handleChange }) => (
            <Form className="space-y-3">
              <p className="text-sm text-muted-foreground">
                {t("Create your account to accept this delegation invitation.")}
              </p>
              {errorMessage && (
                <p className="text-sm text-destructive">{errorMessage}</p>
              )}
              <FormField
                name="name"
                label={t("Name")}
                value={values.name}
                onChange={handleChange}
                error={touched.name && errors.name}
                required
              />
              <FormField
                name="username"
                label={t("Username")}
                value={values.username}
                onChange={handleChange}
                error={touched.username && errors.username}
                required
              />
              <FormField
                name="password"
                type={showPassword ? "text" : "password"}
                label={t("Password")}
                value={values.password}
                onChange={handleChange}
                error={touched.password && errors.password}
                rightIcon={
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="text-muted-foreground hover:text-foreground transition-colors"
                    aria-label={
                      showPassword ? t("Hide password") : t("Show password")
                    }
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5" />
                    ) : (
                      <Eye className="h-5 w-5" />
                    )}
                  </button>
                }
                rightIconClassName="top-[60%]"
                required
              />
              <Button type="submit" className="w-full" disabled={submitting}>
                {submitting ? t("Submitting...") : t("Create Account & Accept")}
              </Button>
            </Form>
          )}
        </Formik>
      );
    }

    return (
      <div className="space-y-4">
        <p className="text-sm text-red-500">
          {errorMessage || t("Unable to accept invitation.")}
        </p>
        <Button className="w-full" onClick={() => navigate(PATHS.LOGIN)}>
          {t("Back to Login")}
        </Button>
      </div>
    );
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <div className="w-full max-w-md overflow-hidden rounded-radius bg-card p-8 shadow-2xl">
        <h1 className="mb-2 text-2xl font-semibold text-primary">
          {t("ESG Delegation Invitation")}
        </h1>
        <p className="mb-6 text-sm text-muted-foreground">
          {t("Accept invitation to collaborate on an ESG report.")}
        </p>
        {renderBody()}
      </div>
    </div>
  );
});

export default ESGInviteAcceptPage;
